import { useLayoutEffect, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastProvider, Toaster, useToast } from '@shining-technologies/ui';
import { registerToastSink, TOAST_LIMIT } from '@/lib/toastStore';

function ToastBridge() {
  const { toast, dismiss, dismissAll } = useToast();
  useEffect(() => registerToastSink({ toast, dismiss, dismissAll }), [toast, dismiss, dismissAll]);
  return null;
}

// Allocates the toaster's portal host element ONLY — no DOM insertion here.
// Guarded for SSR/non-DOM environments (there are none in this app, but a
// lazy initializer running during render should never assume `document`).
// See FIX(admin-toaster-host-render-side-effect) below for why appending
// must not happen in this function.
function createToasterHost(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const el = document.createElement('div');
  el.setAttribute('data-admin-toaster', '');
  el.addEventListener('pointerdown', (event) => event.stopPropagation());
  return el;
}

/**
 * The UI kit's toast stack, mounted ONCE in app/providers.tsx. Raise toasts
 * with `toast` from '@/lib/toast' — never with the kit's `useToast()` (this
 * provider wraps no children, on purpose: the imperative facade is the only
 * entry point, so toasts work from non-component code too).
 *
 * FIX(admin-toaster-portal): the kit's `Toaster` is not portalled, so it sat
 * inside #root. Before: while a Radix modal was open, a click on a toast's
 * Dismiss/action button was an "outside" pointerdown for the dialog and closed
 * it. After: the stack lives in its own element on <body>, and pointerdowns
 * inside it stop there, so Radix's document-level outside-click listener never
 * sees them. (Screen readers were already fine: Radix's aria-hidden pass keeps
 * `[aria-live]` regions, which the kit's toaster region is.)
 *
 * FIX(admin-toaster-host-setstate-in-effect): the host element used to be
 * created inside a mount `useEffect` and handed to `setHost` — a setState
 * synchronously inside an effect, which also means the FIRST render commits
 * with no portal target (the `Toaster` only appears from the second render
 * on). Before: `useState(null)` + create/append/`setState` in an effect.
 *
 * FIX(admin-toaster-host-render-side-effect): the first fix moved the
 * `document.body.appendChild` call INTO the lazy `useState` initializer, but
 * that is still a render-phase side effect — under StrictMode's
 * mount/unmount/remount replay (and under HMR) the initializer can run twice
 * before an effect ever cleans up the first host, leaking a second
 * `[data-admin-toaster]` div on `<body>`. Worse, the cleanup effect below then
 * removed the KEPT host on a replay, leaving `Toaster` portalled into a
 * detached node — toasts render but are invisible. Before: initializer both
 * allocates AND appends. After: the initializer only allocates (`createElement`
 * + the pointerdown listener, no DOM insertion); a `useLayoutEffect` keyed on
 * `[host]` does the actual `appendChild`/`remove`, so StrictMode's
 * mount→unmount→remount replay re-appends the SAME host each time rather than
 * losing track of it, and there is still no setState inside any effect.
 */
export function KitToaster() {
  const [host] = useState<HTMLElement | null>(createToasterHost);

  useLayoutEffect(() => {
    if (!host) return;
    document.body.appendChild(host);
    return () => {
      host.remove();
    };
  }, [host]);

  return (
    <ToastProvider duration={5000} limit={TOAST_LIMIT}>
      <ToastBridge />
      {host && createPortal(<Toaster position="top-right" />, host)}
    </ToastProvider>
  );
}
